import { useState } from "react";
import { format, parseISO, addDays, differenceInCalendarDays, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/** Max days allowed in any single date-range search across the app. */
export const MAX_RANGE_DAYS = 62; // ≈ 2 calendar months

/** Convert a YYYY-MM-DD string ↔ Date, tolerant of empty / bad values. */
const toDate = (v) => {
  if (!v) return undefined;
  const d = typeof v === "string" ? parseISO(v) : v;
  return isValid(d) ? d : undefined;
};
const toISO = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const prettyLabel = (v) => {
  const d = toDate(v);
  return d ? format(d, "d MMM yyyy") : "—";
};

/**
 * Single-date picker: renders a button trigger that opens a shadcn Calendar
 * popover. Emits `onChange(YYYY-MM-DD)` when a day is selected.
 *
 * Props:
 *   value        — current YYYY-MM-DD string (or "")
 *   onChange     — (iso: string) => void
 *   min / max    — optional YYYY-MM-DD bounds
 *   testId       — data-testid on the trigger button
 *   label        — visually-hidden aria label
 *   placeholder  — text when empty (default "Pick a date")
 */
export function DateField({ value, onChange, min, max, testId, label, placeholder }) {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  const minD = toDate(min);
  const maxD = toDate(max);
  const disabled = (day) => {
    if (minD && day < minD) return true;
    if (maxD && day > maxD) return true;
    return false;
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={label || "Pick a date"}
          className="datefield-trigger"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 12px", background: "var(--surface, #141416)",
            border: "1px solid var(--line, #2a2a2d)", borderRadius: 8,
            color: "var(--text, #f5f5f4)", fontSize: 13, cursor: "pointer",
            minWidth: 150,
          }}
        >
          <CalendarIcon size={14} />
          <span style={{ letterSpacing: 0.2 }}>{selected ? prettyLabel(value) : (placeholder || "Pick a date")}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 bg-[var(--surface,#141416)] border-[var(--line,#2a2a2d)] text-[var(--text,#f5f5f4)]" style={{ zIndex: 100 }}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => { if (d) { onChange(toISO(d)); setOpen(false); } }}
          disabled={disabled}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Enforce a max range between start/end. Given the latest user action, clamps
 * the counterpart date so the window never exceeds MAX_RANGE_DAYS.
 *
 * Usage:
 *   const onStart = (v) => setRange(clampRange(v, end, "start"));
 */
export function clampRange(start, end, movedField) {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return { start, end };
  const days = differenceInCalendarDays(e, s);
  if (days < 0) {
    // If start moved past end, snap end to start (single-day search)
    return movedField === "start" ? { start, end: start } : { start: end, end };
  }
  if (days <= MAX_RANGE_DAYS) return { start, end };
  if (movedField === "start") {
    return { start, end: toISO(addDays(s, MAX_RANGE_DAYS)) };
  }
  return { start: toISO(addDays(e, -MAX_RANGE_DAYS)), end };
}

/**
 * Full "From … To" pair with 2-month max enforcement + calendar pop-overs.
 * Emits `onChange({ start, end })` whenever either side is picked.
 */
export function DateRangeField({ start, end, onChange, testPrefix = "range" }) {
  const commit = (next, movedField) => onChange(clampRange(next.start, next.end, movedField));
  return (
    <div className="daterange-field" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      <DateField
        value={start}
        onChange={(v) => commit({ start: v, end }, "start")}
        max={end}
        testId={`${testPrefix}-start`}
        label="Start date"
        placeholder="Start"
      />
      <span style={{ color: "var(--muted, #8a8a8a)", fontSize: 13, padding: "0 2px" }}>→</span>
      <DateField
        value={end}
        onChange={(v) => commit({ start, end: v }, "end")}
        min={start}
        testId={`${testPrefix}-end`}
        label="End date"
        placeholder="End"
      />
    </div>
  );
}
